import React from 'react';
import classNames from 'classnames';
import type { SxProps, Theme } from '@mui/material/styles';

import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';

import {
  HEADER_OFFSET,
  BAR_TOP,
  BAR_HEIGHT,
  GAP_UNDER_BAR,
} from 'helpers/utils/components/constants';

import type { SnappingRect } from 'helpers/utils/components/snapping-bar';

export interface DimMasksProps {
  rect: SnappingRect;
}

const LINE_SX: SxProps<Theme> = {
  position: 'fixed',
  zIndex: 701,
  pointerEvents: 'none',
  bgcolor: 'common.white',
};
const VERTICAL_LINE_SX: SxProps<Theme> = { width: '0.25rem' };
const HORIZONTAL_LINE_SX: SxProps<Theme> = { height: '0.125rem' };

const DimMasks: React.FC<DimMasksProps> = ({ rect }) => {
  const overlayStyle = 'fixed bg-black/60 pointer-events-none z-[700]';

  const viewportBottom =
    window.innerHeight || document.documentElement.clientHeight || 0;

  const clampTop = Math.max(
    HEADER_OFFSET,
    BAR_TOP + BAR_HEIGHT + GAP_UNDER_BAR
  );
  const visibleTop = Math.max(0, Math.max(rect.top, clampTop));
  const visibleBottom = Math.min(rect.bottom, viewportBottom);
  const visibleHeight = Math.max(0, visibleBottom - visibleTop);
  const hasVisibleSlice = visibleHeight > 0;

  return (
    <>
      <div
        className={classNames(overlayStyle, 'top-0 left-0 w-screen')}
        style={{
          height: Math.max(0, visibleTop),
        }}
      />

      <div
        className={classNames(overlayStyle, 'left-0')}
        style={{
          top: visibleTop,
          width: Math.max(0, rect.left),
          height: visibleHeight,
        }}
      />
      <div
        className={overlayStyle}
        style={{
          top: visibleTop,
          left: rect.right,
          width: `calc(100vw - ${rect.right}px)`,
          height: visibleHeight,
        }}
      />

      <div
        className={classNames(overlayStyle, 'left-0 w-screen')}
        style={{
          top: visibleBottom,
          height: `calc(100vh - ${visibleBottom}px)`,
        }}
      />

      {hasVisibleSlice && (
        <>
          <Box
            sx={mergeSx(LINE_SX, VERTICAL_LINE_SX)}
            style={{
              top: visibleTop,
              left: rect.left - 2,
              height: visibleHeight,
            }}
          />
          <Box
            sx={mergeSx(LINE_SX, VERTICAL_LINE_SX)}
            style={{
              top: visibleTop,
              left: rect.right,
              height: visibleHeight,
            }}
          />
          {rect.top >= clampTop &&
            rect.top >= 0 &&
            rect.top <= viewportBottom && (
              <Box
                sx={mergeSx(LINE_SX, HORIZONTAL_LINE_SX)}
                style={{
                  top: rect.top,
                  left: rect.left - 2,
                  width: rect.width,
                }}
              />
            )}
          {rect.bottom <= viewportBottom && rect.bottom >= HEADER_OFFSET && (
            <Box
              sx={mergeSx(LINE_SX, HORIZONTAL_LINE_SX)}
              style={{
                top: rect.bottom - 1,
                left: rect.left - 2,
                width: rect.width,
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default DimMasks;
